/**
 * HashtagGenerator.tsx
 *
 * Modal component for generating hashtag suggestions using AI.
 * Allows users to enter a seed term, select country, and generate up to 30 hashtags.
 * Users can select/deselect hashtags and add them to the caption.
 *
 * Features:
 * - Input field for seed term (e.g., "summer")
 * - Country selector dropdown (optional, default: US)
 * - Generate button with loading state
 * - Clickable hashtag badges (selected vs unselected)
 * - Add button to insert selected hashtags into caption
 * - Error handling with toast notifications
 * - Empty results handling
 *
 * Usage:
 * ```tsx
 * <HashtagGenerator
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onHashtagsAdded={(hashtags) => console.log(hashtags)}
 * />
 * ```
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { Input } from '@/components/ui/Input';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { hashtagsService, HashtagResult } from '@/services/hashtags.service';
import { isGlobalGatingHandledError } from '@/services/api';
import { cn } from '@/utils/cn';

/**
 * Country option interface
 */
interface CountryOption {
  value: string;
  label: string;
  flag: string;
  region: 'Americas' | 'Europe' | 'Asia' | 'Middle East' | 'Africa' | 'Oceania';
  searchTerms: string[];
}

interface HashtagGeneratorProps {
  /**
   * Whether the modal is open
   */
  open: boolean;

  /**
   * Callback when modal should close
   */
  onClose: () => void;

  /**
   * Callback when hashtags are added to caption
   * Receives array of selected hashtags (without # prefix)
   */
  onHashtagsAdded: (hashtags: string[]) => void;

  /**
   * Whether this modal is nested inside another modal
   */
  nested?: boolean;
}

/**
 * Comprehensive list of countries for hashtag generation
 * Organized by region with flags and search terms
 */
const COUNTRIES: CountryOption[] = [
  // Americas
  { value: 'US', label: 'United States', flag: '🇺🇸', region: 'Americas', searchTerms: ['usa', 'america', 'united states', 'us'] },
  { value: 'CA', label: 'Canada', flag: '🇨🇦', region: 'Americas', searchTerms: ['canada', 'canadian'] },
  { value: 'MX', label: 'Mexico', flag: '🇲🇽', region: 'Americas', searchTerms: ['mexico', 'mexican'] },
  { value: 'BR', label: 'Brazil', flag: '🇧🇷', region: 'Americas', searchTerms: ['brazil', 'brazilian', 'brasil'] },
  { value: 'AR', label: 'Argentina', flag: '🇦🇷', region: 'Americas', searchTerms: ['argentina', 'argentinian'] },
  { value: 'CL', label: 'Chile', flag: '🇨🇱', region: 'Americas', searchTerms: ['chile', 'chilean'] },
  { value: 'CO', label: 'Colombia', flag: '🇨🇴', region: 'Americas', searchTerms: ['colombia', 'colombian'] },
  { value: 'PE', label: 'Peru', flag: '🇵🇪', region: 'Americas', searchTerms: ['peru', 'peruvian'] },
  { value: 'VE', label: 'Venezuela', flag: '🇻🇪', region: 'Americas', searchTerms: ['venezuela', 'venezuelan'] },
  { value: 'EC', label: 'Ecuador', flag: '🇪🇨', region: 'Americas', searchTerms: ['ecuador', 'ecuadorian'] },
  { value: 'UY', label: 'Uruguay', flag: '🇺🇾', region: 'Americas', searchTerms: ['uruguay', 'uruguayan'] },
  { value: 'CR', label: 'Costa Rica', flag: '🇨🇷', region: 'Americas', searchTerms: ['costa rica', 'costa rican'] },
  { value: 'PA', label: 'Panama', flag: '🇵🇦', region: 'Americas', searchTerms: ['panama', 'panamanian'] },

  // Europe
  { value: 'GB', label: 'United Kingdom', flag: '🇬🇧', region: 'Europe', searchTerms: ['uk', 'united kingdom', 'britain', 'england', 'british'] },
  { value: 'DE', label: 'Germany', flag: '🇩🇪', region: 'Europe', searchTerms: ['germany', 'german', 'deutschland'] },
  { value: 'FR', label: 'France', flag: '🇫🇷', region: 'Europe', searchTerms: ['france', 'french'] },
  { value: 'ES', label: 'Spain', flag: '🇪🇸', region: 'Europe', searchTerms: ['spain', 'spanish', 'espana'] },
  { value: 'IT', label: 'Italy', flag: '🇮🇹', region: 'Europe', searchTerms: ['italy', 'italian', 'italia'] },
  { value: 'NL', label: 'Netherlands', flag: '🇳🇱', region: 'Europe', searchTerms: ['netherlands', 'dutch', 'holland'] },
  { value: 'BE', label: 'Belgium', flag: '🇧🇪', region: 'Europe', searchTerms: ['belgium', 'belgian'] },
  { value: 'PL', label: 'Poland', flag: '🇵🇱', region: 'Europe', searchTerms: ['poland', 'polish'] },
  { value: 'SE', label: 'Sweden', flag: '🇸🇪', region: 'Europe', searchTerms: ['sweden', 'swedish'] },
  { value: 'NO', label: 'Norway', flag: '🇳🇴', region: 'Europe', searchTerms: ['norway', 'norwegian'] },
  { value: 'DK', label: 'Denmark', flag: '🇩🇰', region: 'Europe', searchTerms: ['denmark', 'danish'] },
  { value: 'FI', label: 'Finland', flag: '🇫🇮', region: 'Europe', searchTerms: ['finland', 'finnish'] },
  { value: 'AT', label: 'Austria', flag: '🇦🇹', region: 'Europe', searchTerms: ['austria', 'austrian'] },
  { value: 'CH', label: 'Switzerland', flag: '🇨🇭', region: 'Europe', searchTerms: ['switzerland', 'swiss'] },
  { value: 'PT', label: 'Portugal', flag: '🇵🇹', region: 'Europe', searchTerms: ['portugal', 'portuguese'] },
  { value: 'IE', label: 'Ireland', flag: '🇮🇪', region: 'Europe', searchTerms: ['ireland', 'irish'] },
  { value: 'GR', label: 'Greece', flag: '🇬🇷', region: 'Europe', searchTerms: ['greece', 'greek'] },
  { value: 'CZ', label: 'Czech Republic', flag: '🇨🇿', region: 'Europe', searchTerms: ['czech', 'czech republic', 'czechia'] },
  { value: 'RO', label: 'Romania', flag: '🇷🇴', region: 'Europe', searchTerms: ['romania', 'romanian'] },
  { value: 'HU', label: 'Hungary', flag: '🇭🇺', region: 'Europe', searchTerms: ['hungary', 'hungarian'] },
  { value: 'UA', label: 'Ukraine', flag: '🇺🇦', region: 'Europe', searchTerms: ['ukraine', 'ukrainian'] },
  { value: 'RU', label: 'Russia', flag: '🇷🇺', region: 'Europe', searchTerms: ['russia', 'russian'] },

  // Asia
  { value: 'JP', label: 'Japan', flag: '🇯🇵', region: 'Asia', searchTerms: ['japan', 'japanese'] },
  { value: 'KR', label: 'South Korea', flag: '🇰🇷', region: 'Asia', searchTerms: ['korea', 'south korea', 'korean'] },
  { value: 'CN', label: 'China', flag: '🇨🇳', region: 'Asia', searchTerms: ['china', 'chinese'] },
  { value: 'IN', label: 'India', flag: '🇮🇳', region: 'Asia', searchTerms: ['india', 'indian'] },
  { value: 'SG', label: 'Singapore', flag: '🇸🇬', region: 'Asia', searchTerms: ['singapore', 'singaporean'] },
  { value: 'HK', label: 'Hong Kong', flag: '🇭🇰', region: 'Asia', searchTerms: ['hong kong', 'hongkong', 'hk'] },
  { value: 'TW', label: 'Taiwan', flag: '🇹🇼', region: 'Asia', searchTerms: ['taiwan', 'taiwanese'] },
  { value: 'TH', label: 'Thailand', flag: '🇹🇭', region: 'Asia', searchTerms: ['thailand', 'thai'] },
  { value: 'VN', label: 'Vietnam', flag: '🇻🇳', region: 'Asia', searchTerms: ['vietnam', 'vietnamese'] },
  { value: 'PH', label: 'Philippines', flag: '🇵🇭', region: 'Asia', searchTerms: ['philippines', 'filipino', 'philippine'] },
  { value: 'MY', label: 'Malaysia', flag: '🇲🇾', region: 'Asia', searchTerms: ['malaysia', 'malaysian'] },
  { value: 'ID', label: 'Indonesia', flag: '🇮🇩', region: 'Asia', searchTerms: ['indonesia', 'indonesian'] },
  { value: 'PK', label: 'Pakistan', flag: '🇵🇰', region: 'Asia', searchTerms: ['pakistan', 'pakistani'] },
  { value: 'BD', label: 'Bangladesh', flag: '🇧🇩', region: 'Asia', searchTerms: ['bangladesh', 'bangladeshi'] },

  // Middle East
  { value: 'AE', label: 'United Arab Emirates', flag: '🇦🇪', region: 'Middle East', searchTerms: ['uae', 'emirates', 'dubai', 'abu dhabi'] },
  { value: 'SA', label: 'Saudi Arabia', flag: '🇸🇦', region: 'Middle East', searchTerms: ['saudi', 'saudi arabia', 'ksa'] },
  { value: 'IL', label: 'Israel', flag: '🇮🇱', region: 'Middle East', searchTerms: ['israel', 'israeli'] },
  { value: 'TR', label: 'Turkey', flag: '🇹🇷', region: 'Middle East', searchTerms: ['turkey', 'turkish', 'turkiye'] },
  { value: 'EG', label: 'Egypt', flag: '🇪🇬', region: 'Middle East', searchTerms: ['egypt', 'egyptian'] },
  { value: 'QA', label: 'Qatar', flag: '🇶🇦', region: 'Middle East', searchTerms: ['qatar', 'qatari'] },
  { value: 'KW', label: 'Kuwait', flag: '🇰🇼', region: 'Middle East', searchTerms: ['kuwait', 'kuwaiti'] },

  // Africa
  { value: 'ZA', label: 'South Africa', flag: '🇿🇦', region: 'Africa', searchTerms: ['south africa', 'south african'] },
  { value: 'NG', label: 'Nigeria', flag: '🇳🇬', region: 'Africa', searchTerms: ['nigeria', 'nigerian'] },
  { value: 'KE', label: 'Kenya', flag: '🇰🇪', region: 'Africa', searchTerms: ['kenya', 'kenyan'] },
  { value: 'MA', label: 'Morocco', flag: '🇲🇦', region: 'Africa', searchTerms: ['morocco', 'moroccan'] },
  { value: 'GH', label: 'Ghana', flag: '🇬🇭', region: 'Africa', searchTerms: ['ghana', 'ghanaian'] },
  { value: 'ET', label: 'Ethiopia', flag: '🇪🇹', region: 'Africa', searchTerms: ['ethiopia', 'ethiopian'] },

  // Oceania
  { value: 'AU', label: 'Australia', flag: '🇦🇺', region: 'Oceania', searchTerms: ['australia', 'australian', 'aussie'] },
  { value: 'NZ', label: 'New Zealand', flag: '🇳🇿', region: 'Oceania', searchTerms: ['new zealand', 'nz', 'kiwi'] },
];

/**
 * HashtagGenerator Component
 * Modal for generating and selecting hashtags
 */
export const HashtagGenerator = ({
  open,
  onClose,
  onHashtagsAdded,
  nested = false,
}: HashtagGeneratorProps): JSX.Element => {
  const { t } = useTranslation('common');

  const [term, setTerm] = useState('');
  const [country, setCountry] = useState('US');
  const [suggestions, setSuggestions] = useState<HashtagResult[]>([]);
  const [selectedHashtags, setSelectedHashtags] = useState<Set<string>>(
    new Set()
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Country selector state
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const countrySearchInputRef = useRef<HTMLInputElement>(null);
  const countryListRef = useRef<HTMLDivElement>(null);

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return COUNTRIES;
    const searchLower = countrySearch.toLowerCase().slice(0, 100);
    return COUNTRIES.filter(
      (c) =>
        c.label.toLowerCase().includes(searchLower) ||
        c.value.toLowerCase().includes(searchLower) ||
        c.searchTerms.some((term) => term.includes(searchLower))
    );
  }, [countrySearch]);

  // Group countries by region
  const groupedCountries = useMemo(() => {
    const groups: Record<string, CountryOption[]> = {
      Americas: [],
      Europe: [],
      Asia: [],
      'Middle East': [],
      Africa: [],
      Oceania: [],
    };
    filteredCountries.forEach((c) => {
      groups[c.region].push(c);
    });
    return groups;
  }, [filteredCountries]);

  const selectedCountry = COUNTRIES.find((c) => c.value === country);

  // Focus search input when country popover opens
  useEffect(() => {
    if (isCountryOpen && countrySearchInputRef.current) {
      setTimeout(() => countrySearchInputRef.current?.focus(), 0);
    }
  }, [isCountryOpen]);

  // Reset search when country popover closes
  useEffect(() => {
    if (!isCountryOpen) {
      setCountrySearch('');
      setHighlightedIndex(-1);
    }
  }, [isCountryOpen]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && countryListRef.current) {
      const highlightedElement = countryListRef.current.querySelector(
        `[data-option-index="${highlightedIndex}"]`
      );
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  /**
   * Reset form state
   */
  const resetForm = () => {
    setTerm('');
    setCountry('US');
    setSuggestions([]);
    setSelectedHashtags(new Set());
    setError(null);
    setIsGenerating(false);
  };

  /**
   * Select all visible hashtags
   */
  const handleSelectAll = () => {
    const allHashtags = new Set(selectedHashtags);
    suggestions.forEach((hashtag) => {
      allHashtags.add(hashtag.value);
    });
    setSelectedHashtags(allHashtags);
  };

  /**
   * Deselect all hashtags
   */
  const handleDeselectAll = () => {
    setSelectedHashtags(new Set());
  };

  /**
   * Calculate total character count of selected hashtags (including # and spaces)
   */
  const totalCharacterCount = useMemo(() => {
    return Array.from(selectedHashtags).reduce((sum, hashtag) => {
      return sum + hashtag.length + 1 + 1; // +1 for #, +1 for space
    }, 0);
  }, [selectedHashtags]);

  /**
   * Handle modal close
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  /**
   * Generate hashtags
   */
  const handleGenerate = async () => {
    if (!term.trim()) {
      setError(t('hashtag_error_empty_term'));
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const hashtags = await hashtagsService.generate({
        term: term.trim(),
        country,
      });

      if (hashtags.length === 0) {
        setError(t('hashtag_error_no_results'));
      } else {
        // Merge new suggestions with existing ones (avoid duplicates)
        const existingMap = new Map(suggestions.map(h => [h.value, h]));
        hashtags.forEach(h => {
          if (!existingMap.has(h.value)) {
            existingMap.set(h.value, h);
          }
        });
        setSuggestions(Array.from(existingMap.values()));
      }
    } catch (err) {
      if (isGlobalGatingHandledError(err)) return;
      const errorMessage =
        err instanceof Error ? err.message : t('hashtag_error_generation_failed');
      setError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Toggle hashtag selection
   */
  const toggleHashtag = (hashtag: string) => {
    const newSelected = new Set(selectedHashtags);
    if (newSelected.has(hashtag)) {
      newSelected.delete(hashtag);
    } else {
      newSelected.add(hashtag);
    }
    setSelectedHashtags(newSelected);
  };

  /**
   * Add selected hashtags to caption
   */
  const handleAddHashtags = () => {
    if (selectedHashtags.size === 0) {
      setError(t('hashtag_error_no_selection'));
      return;
    }

    onHashtagsAdded(Array.from(selectedHashtags));
    handleClose();
  };

  /**
   * Handle Enter key in input field
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isGenerating && term.trim()) {
      e.preventDefault();
      handleGenerate();
    }
  };

  /**
   * Handle country selection
   */
  const handleSelectCountry = (countryCode: string) => {
    setCountry(countryCode);
    setIsCountryOpen(false);
  };

  /**
   * Handle keyboard navigation in country dropdown
   */
  const handleCountryKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredCountries.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredCountries.length) {
          handleSelectCountry(filteredCountries[highlightedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsCountryOpen(false);
        break;
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(open) => !open && handleClose()}
      title={t('hashtag_modal_title')}
      size="lg"
      nested={nested}
      footer={
        <div className="flex items-center justify-end gap-1.5 sm:gap-2">
          <CancelButton onClick={handleClose} disabled={isGenerating} tooltip="Cancel" />
          <Button
            variant="primary"
            onClick={handleAddHashtags}
            disabled={selectedHashtags.size === 0 || isGenerating}
          >
            <Icon name={ICONS.PLUS} size="sm" className="mr-2" />
            {t('hashtag_add_selected', { count: selectedHashtags.size })}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 lg:space-y-6">
        {/* Error Message */}
        {error && (
          <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
            <Icon
              name={ICONS.CAUTION}
              size="sm"
              color="text-red-600"
              className="flex-shrink-0 mt-0.5"
            />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Warning: Too Many Hashtags */}
        {selectedHashtags.size > 30 && (
          <div className="flex items-start gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <Icon
              name={ICONS.CAUTION}
              size="sm"
              color="text-amber-600"
              className="flex-shrink-0 mt-0.5"
            />
            <p className="text-sm text-amber-600">
              {t('hashtag_warning_too_many', {
                count: selectedHashtags.size,
                defaultValue: `You have selected ${selectedHashtags.size} hashtags. Instagram allows max 30 per post.`
              })}
            </p>
          </div>
        )}

        {/* Input Section */}
        <div className="space-y-4">
          <div>
            <label className="block text-xs lg:text-sm font-medium text-bs-dark mb-1 lg:mb-2">
              {t('hashtag_seed_term_label')}
            </label>
            <Input
              inputSize="sm"
              type="text"
              placeholder={t('hashtag_seed_term_placeholder')}
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isGenerating}
              className="text-sm"
            />
            <p className="mt-1 text-xs text-bs-gray">
              {t('hashtag_seed_term_hint')}
            </p>
          </div>

          <div>
            <label className="block text-xs lg:text-sm font-medium text-bs-dark mb-1 lg:mb-2">
              {t('hashtag_country_label')}
            </label>
            <Popover.Root open={isCountryOpen} onOpenChange={(open) => !isGenerating && setIsCountryOpen(open)}>
              <Popover.Trigger asChild>
                <button
                  type="button"
                  disabled={isGenerating}
                  className={cn(
                    'w-full px-3 lg:px-4 h-[34px] lg:h-auto lg:py-2.5 text-left text-xs lg:text-sm border border-gray-300 rounded-xl',
                    'flex items-center justify-between gap-2',
                    isGenerating
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : 'bg-white hover:border-gray-400 focus:outline-none focus:border-gray-400'
                  )}
                >
                  <span className="truncate flex items-center gap-2">
                    {selectedCountry ? (
                      <>
                        <span className="text-xl">{selectedCountry.flag}</span>
                        <span>{selectedCountry.label}</span>
                      </>
                    ) : (
                      <span className="text-gray-400">{t('hashtag_country_placeholder')}</span>
                    )}
                  </span>
                  <Icon
                    name={ICONS.ANGLE_DOWN}
                    size="sm"
                    className={cn(
                      'flex-shrink-0 transition-transform',
                      isCountryOpen && 'rotate-180'
                    )}
                    ariaHidden
                  />
                </button>
              </Popover.Trigger>

              <Popover.Portal>
                <Popover.Content
                  className="z-[9999] w-[var(--radix-popover-trigger-width)] bg-white border border-gray-200 rounded-xl shadow-lg"
                  sideOffset={4}
                  align="start"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  {/* Search input */}
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <Icon
                        name={ICONS.SEARCH}
                        size="sm"
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        ariaHidden
                      />
                      <input
                        ref={countrySearchInputRef}
                        type="text"
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        onKeyDown={handleCountryKeyDown}
                        placeholder="Search countries..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                      />
                    </div>
                  </div>

                  {/* Options list */}
                  <div
                    ref={countryListRef}
                    className="max-h-64 overflow-y-auto overscroll-contain"
                    onWheel={(e) => e.stopPropagation()}
                  >
                    {filteredCountries.length === 0 ? (
                      <div className="p-4 text-center text-bs-gray">
                        <p className="text-sm">{t('hashtag_no_countries_found')}</p>
                      </div>
                    ) : (
                      (() => {
                        let optionIndex = 0;
                        return Object.entries(groupedCountries).map(([region, countries]) =>
                          countries.length > 0 ? (
                            <div key={region}>
                              <div className="px-3 py-1.5 text-xs font-semibold text-bs-gray bg-off-white sticky top-0">
                                {region}
                              </div>
                              {countries.map((c) => {
                                const currentIndex = optionIndex++;
                                const isSelected = country === c.value;
                                const isHighlighted = currentIndex === highlightedIndex;

                                return (
                                  <button
                                    key={c.value}
                                    type="button"
                                    onClick={() => handleSelectCountry(c.value)}
                                    data-option-index={currentIndex}
                                    className={cn(
                                      'w-full px-3 py-2 text-left transition-colors border-l-2',
                                      isSelected && 'bg-soft-mint text-dark-teal',
                                      isHighlighted && !isSelected && 'bg-soft-mint',
                                      !isSelected && !isHighlighted && 'hover:bg-off-white',
                                      isSelected ? 'border-dark-teal' : 'border-transparent'
                                    )}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className="text-xl flex-shrink-0">{c.flag}</span>
                                      <span className="font-medium truncate text-sm">
                                        {c.label}
                                      </span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          ) : null
                        );
                      })()
                    )}
                  </div>
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          </div>

          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={handleGenerate}
              disabled={!term.trim() || isGenerating}
              loading={isGenerating}
            >
              {!isGenerating && <Icon name={ICONS.SPARK} size="sm" className="mr-2" />}
              {isGenerating ? t('hashtag_generating') : t('hashtag_generate_button')}
            </Button>
          </div>
        </div>

        {/* Suggestions Section */}
        {suggestions.length > 0 && (
          <div className="space-y-3">
            {/* Header with count and actions */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium text-bs-dark">
                  {t('hashtag_suggestions_title', { count: suggestions.length })}
                </h3>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-soft-mint text-dark-teal">
                  {t('hashtag_selected_count', {
                    count: selectedHashtags.size,
                    defaultValue: `${selectedHashtags.size} selected`
                  })}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSelectAll}
                  className="text-xs text-dark-teal hover:text-bs-black font-medium"
                >
                  {t('hashtag_select_all', { defaultValue: 'Select All' })}
                </button>
                {selectedHashtags.size > 0 && (
                  <button
                    onClick={handleDeselectAll}
                    className="text-xs text-dark-teal hover:text-bs-black font-medium"
                  >
                    {t('hashtag_clear_selection', { defaultValue: 'Deselect All' })}
                  </button>
                )}
              </div>
            </div>

            {/* Character count info */}
            {selectedHashtags.size > 0 && (
              <div className="text-xs text-bs-gray flex items-center justify-between px-1">
                <span>
                  {t('hashtag_char_count', {
                    count: totalCharacterCount,
                    defaultValue: `Total characters: ${totalCharacterCount}`
                  })}
                </span>
              </div>
            )}

            {/* Selected Hashtags (Pinned) */}
            {selectedHashtags.size > 0 && (
              <div className="space-y-2 pb-2 border-b border-gray-200">
                <h4 className="text-xs font-semibold text-bs-gray-stone uppercase tracking-wide">
                  {t('hashtag_selected_section', {
                    count: selectedHashtags.size,
                    defaultValue: `Selected (${selectedHashtags.size})`
                  })}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {Array.from(selectedHashtags).map((value) => (
                    <button
                      key={value}
                      onClick={() => toggleHashtag(value)}
                      className="inline-flex items-center px-3 py-2 rounded-full text-sm font-medium transition-all bg-soft-mint text-dark-teal hover:bg-bs-secondary focus:outline-none"
                      aria-pressed={true}
                      aria-label={t('hashtag_deselect_aria', { value })}
                    >
                      #{value.replace(/^#/, '')}
                      <Icon
                        name={ICONS.CHECK}
                        size="xs"
                        className="ml-1.5"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Unselected Hashtag Badges */}
            <div className="flex flex-wrap gap-2 max-h-96 overflow-y-auto p-2">
              {suggestions
                .filter((hashtag) => !selectedHashtags.has(hashtag.value))
                .map((hashtag) => (
                  <button
                    key={hashtag.id}
                    onClick={() => toggleHashtag(hashtag.value)}
                    className="inline-flex items-center px-3 py-2 rounded-full text-sm font-medium transition-all bg-off-white text-bs-dark hover:bg-soft-mint focus:outline-none"
                    aria-pressed={false}
                    aria-label={t('hashtag_select_aria', { value: hashtag.value })}
                  >
                    #{hashtag.value.replace(/^#/, '')}
                  </button>
                ))}
            </div>

            <p className="text-xs text-bs-gray text-center">
              {t('hashtag_click_hint')}
            </p>
          </div>
        )}

        {/* Empty State (after generation) */}
        {!isGenerating && suggestions.length === 0 && !error && term && (
          <div className="text-center py-8 text-bs-gray">
            <Icon name={ICONS.SPARK} size="lg" className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">{t('hashtag_empty_state')}</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default HashtagGenerator;
